import { useState } from 'react'
import { Link } from 'react-router'
import {
  useCompanies,
  useCompanyMembers,
  useCreateCompany,
  useDirectory,
  useUpdateCompany,
} from '../api/hooks'
import { useAuth } from '../auth/AuthProvider'
import type { Pillar } from '../api/types'
import {
  Button,
  Chip,
  EmptyState,
  ErrorNotice,
  Loading,
  PILLAR_LABEL,
  PageHeader,
} from '../components/ui'

const PILLARS: Pillar[] = ['LOGISTICS', 'PROJECTS', 'INVESTMENTS', 'OTHER']

export function CompaniesPage() {
  const { user } = useAuth()
  const isAdmin = user?.role === 'ADMIN'
  const { data: companies, isLoading, error } = useCompanies()
  const [creating, setCreating] = useState(false)

  return (
    <>
      <PageHeader
        title="Companies"
        subtitle="The entities work is filed under. People can belong to several."
        actions={
          isAdmin ? (
            <Button variant="primary" onClick={() => setCreating((v) => !v)}>
              {creating ? 'Cancel' : 'New company'}
            </Button>
          ) : undefined
        }
      />

      {creating && isAdmin && <NewCompanyForm onDone={() => setCreating(false)} />}

      {isLoading && <Loading />}
      {error && <ErrorNotice error={error} />}

      {companies && companies.length === 0 && (
        <EmptyState
          title="No companies yet"
          description={
            isAdmin
              ? 'Add your entities first. Projects and tasks hang off them.'
              : 'You have not been added to any company yet. Ask your administrator.'
          }
          action={
            isAdmin ? (
              <Button variant="primary" onClick={() => setCreating(true)}>
                Add the first company
              </Button>
            ) : undefined
          }
        />
      )}

      <div className="space-y-3">
        {companies?.map((company) => (
          <CompanyCard key={company.id} company={company} isAdmin={isAdmin} />
        ))}
      </div>
    </>
  )
}

function CompanyCard({
  company,
  isAdmin,
}: {
  company: NonNullable<ReturnType<typeof useCompanies>['data']>[number]
  isAdmin: boolean
}) {
  const [expanded, setExpanded] = useState(false)
  const { data: people } = useDirectory()
  const { add, update, remove } = useCompanyMembers()
  const updateCompany = useUpdateCompany()

  const [userId, setUserId] = useState('')
  const [title, setTitle] = useState('')
  const [canSchedule, setCanSchedule] = useState(false)

  const memberIds = new Set(company.members.map((m) => m.id))
  const candidates = (people ?? []).filter((p) => !memberIds.has(p.id))

  return (
    <div className="card p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-base font-semibold tracking-tight">{company.name}</h2>
            <Chip tone="accent">{PILLAR_LABEL[company.pillar]}</Chip>
            {!company.isActive && <Chip title="Archived">Archived</Chip>}
          </div>
          {company.notes && (
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{company.notes}</p>
          )}
          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
            {company.projectCount} project{company.projectCount === 1 ? '' : 's'} ·{' '}
            {company.openTaskCount} open task{company.openTaskCount === 1 ? '' : 's'} ·{' '}
            {company.members.length} member{company.members.length === 1 ? '' : 's'}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <Link
            to={`/projects?company=${company.id}`}
            className="text-sm text-brand-700 hover:underline dark:text-brand-300"
          >
            Projects
          </Link>
          <Button size="sm" onClick={() => setExpanded((v) => !v)}>
            {expanded ? 'Hide people' : 'People'}
          </Button>
        </div>
      </div>

      {expanded && (
        <div className="mt-4 border-t border-slate-200 pt-4 dark:border-slate-800">
          {isAdmin && (
            <form
              className="mb-3 flex flex-wrap items-end gap-2"
              onSubmit={(e) => {
                e.preventDefault()
                if (!userId) return
                add.mutate(
                  {
                    companyId: company.id,
                    userId,
                    title: title.trim() || undefined,
                    canSchedule,
                  },
                  {
                    onSuccess: () => {
                      setUserId('')
                      setTitle('')
                      setCanSchedule(false)
                    },
                  },
                )
              }}
            >
              <div className="min-w-44 flex-1">
                <label className="label" htmlFor={`add-${company.id}`}>
                  Add person
                </label>
                <select
                  id={`add-${company.id}`}
                  className="field"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                >
                  <option value="">Choose…</option>
                  {candidates.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="min-w-44 flex-1">
                <label className="label" htmlFor={`title-${company.id}`}>
                  Title at this entity
                </label>
                <input
                  id={`title-${company.id}`}
                  className="field"
                  placeholder="e.g. Director"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>

              <label className="flex items-center gap-2 pb-2 text-sm">
                <input
                  type="checkbox"
                  className="rounded border-slate-300 dark:border-slate-600"
                  checked={canSchedule}
                  onChange={(e) => setCanSchedule(e.target.checked)}
                />
                Can schedule meetings
              </label>

              <Button type="submit" variant="primary" loading={add.isPending} disabled={!userId}>
                Add
              </Button>
            </form>
          )}

          {add.error && <ErrorNotice error={add.error} />}
          {update.error && <ErrorNotice error={update.error} />}

          {isAdmin && (
            <p className="mb-2 text-xs text-slate-500 dark:text-slate-400">
              Can schedule meetings lets that person book meetings for people at this entity. It
              takes effect straight away.
            </p>
          )}

          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {company.members.length === 0 && (
              <li className="py-3 text-sm text-slate-500 dark:text-slate-400">
                Nobody is assigned to this company yet.
              </li>
            )}
            {company.members.map((member) => (
              <li key={member.id} className="flex flex-wrap items-center gap-3 py-2.5">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">
                    {member.name}
                    {member.role === 'ADMIN' && (
                      <span className="ml-2 text-xs text-brand-700 dark:text-brand-300">admin</span>
                    )}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {member.title ?? 'No title set'} · {member.email}
                  </p>
                </div>
                {isAdmin && (
                  <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                    <input
                      type="checkbox"
                      className="rounded border-slate-300 dark:border-slate-600"
                      checked={member.canSchedule}
                      disabled={update.isPending}
                      onChange={(e) =>
                        update.mutate({
                          companyId: company.id,
                          userId: member.id,
                          canSchedule: e.target.checked,
                        })
                      }
                    />
                    Can schedule meetings
                  </label>
                )}
                {isAdmin && (
                  <Button
                    variant="danger"
                    size="sm"
                    loading={remove.isPending}
                    onClick={() => remove.mutate({ companyId: company.id, userId: member.id })}
                  >
                    Remove
                  </Button>
                )}
              </li>
            ))}
          </ul>

          {isAdmin && (
            <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-200 pt-3 dark:border-slate-800">
              <Button
                size="sm"
                loading={updateCompany.isPending}
                onClick={() =>
                  updateCompany.mutate({ id: company.id, isActive: !company.isActive })
                }
              >
                {company.isActive ? 'Archive company' : 'Reactivate company'}
              </Button>
              <span className="text-xs text-slate-500 dark:text-slate-400">
                Archiving hides it from the pickers without touching its history.
              </span>
            </div>
          )}
          {updateCompany.error && <ErrorNotice error={updateCompany.error} />}
        </div>
      )}
    </div>
  )
}

function NewCompanyForm({ onDone }: { onDone: () => void }) {
  const createCompany = useCreateCompany()
  const [name, setName] = useState('')
  const [shortName, setShortName] = useState('')
  const [pillar, setPillar] = useState<Pillar>('OTHER')
  const [notes, setNotes] = useState('')

  return (
    <form
      className="card mb-6 space-y-3 p-4"
      onSubmit={(e) => {
        e.preventDefault()
        if (!name.trim()) return
        createCompany.mutate(
          {
            name: name.trim(),
            shortName: shortName.trim() || undefined,
            pillar,
            notes: notes.trim() || null,
          },
          { onSuccess: onDone },
        )
      }}
    >
      <h2 className="text-sm font-semibold">New company</h2>

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="sm:col-span-2">
          <label className="label" htmlFor="c-name">
            Company name
          </label>
          <input
            id="c-name"
            className="field"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>

        <div>
          <label className="label" htmlFor="c-short">
            Short label
          </label>
          <input
            id="c-short"
            className="field"
            placeholder="Shown on task chips"
            value={shortName}
            onChange={(e) => setShortName(e.target.value)}
          />
        </div>

        <div>
          <label className="label" htmlFor="c-pillar">
            Pillar
          </label>
          <select
            id="c-pillar"
            className="field"
            value={pillar}
            onChange={(e) => setPillar(e.target.value as Pillar)}
          >
            {PILLARS.map((p) => (
              <option key={p} value={p}>
                {PILLAR_LABEL[p]}
              </option>
            ))}
          </select>
        </div>

        <div className="sm:col-span-2">
          <label className="label" htmlFor="c-notes">
            Notes
          </label>
          <input
            id="c-notes"
            className="field"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
      </div>

      {createCompany.error && <ErrorNotice error={createCompany.error} />}

      <div className="flex gap-2">
        <Button type="submit" variant="primary" loading={createCompany.isPending} disabled={!name.trim()}>
          Create company
        </Button>
        <Button type="button" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  )
}
